import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useReducedMotion } from 'motion/react';
import { launchAgentWithWidget, type Agent } from '@/entities/agent';
import { useShiftDeleteToolShortcut, useUpsertToolShortcut } from '../../hooks/useToolShortcuts';
import type { ToolShortcut } from '../../api/toolShortcuts.api';
import { EditingShortcutSlot } from './EditingShortcutSlot';
import { EmptyShortcutSlot } from './EmptyShortcutSlot';
import { FilledShortcutSlot } from './FilledShortcutSlot';

// Mirrors `--duration-fast` (150ms), the token `shortcutOut` composes its
// animation from — the removal is delayed by exactly this long so the CSS
// exit animation finishes playing before the tile actually disappears.
const EXIT_ANIMATION_MS = 150;

interface ShortcutSlotProps {
  slotIndex: number;
  agentId: string | null;
  disabled?: boolean;
  slots: readonly (ToolShortcut | null)[];
  availableAgents: Agent[];
  launchHandlers: Partial<Record<string, () => void>>;
}

export function ShortcutSlot({
  slotIndex,
  agentId,
  disabled = false,
  slots,
  availableAgents,
  launchHandlers,
}: ShortcutSlotProps) {
  const navigate = useNavigate();
  const upsertMutation = useUpsertToolShortcut();
  const deleteMutation = useShiftDeleteToolShortcut();
  const shouldReduceMotion = useReducedMotion();

  const [isEditing, setIsEditing] = useState(false);
  // Plays the filled tile's settle-in animation exactly once, right after a
  // confirm — not on every render (e.g. a page reload) it happens to appear on.
  const [justSaved, setJustSaved] = useState(false);
  // Plays the shrink-and-fade exit before the tile is actually removed. Reset
  // once the removal fires so a shifted-in shortcut never inherits it.
  const [isExiting, setIsExiting] = useState(false);

  // Closes the exit-animation delay to a second click. `deleteMutation.isPending`
  // cannot: it stays false until the timer fires, the menu item stays clickable
  // meanwhile (its `onSelect` preventDefault keeps the menu open, and the tile's
  // `pointer-events-none` never reaches the portalled menu), and a ref flips
  // synchronously where state would not. Two removals started from the same
  // `slots` run two interleaved shift chains over five non-transactional
  // requests, and the layout they leave on the server survives a reload.
  const removalStartedRef = useRef(false);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(removeTimerRef.current), []);

  const handleRemove = () => {
    if (removalStartedRef.current) return;
    removalStartedRef.current = true;

    // Both flags clear when the mutation settles, not from the timer: the timer
    // only guarantees the exit animation has played, not that the removal has
    // landed, and clearing there raced the mutation into a frame of plain filled
    // tile before the data caught up. Settled rather than successful, because
    // the exit holds its faded-out, `pointer-events-none` end state — a tile
    // left exiting after a failed removal is invisible and unclickable for good.
    const removeNow = () =>
      deleteMutation.mutate(
        { slotIndex, slots },
        {
          onSettled: () => {
            removalStartedRef.current = false;
            setIsExiting(false);
          },
        },
      );

    if (shouldReduceMotion) {
      removeNow();
      return;
    }
    setIsExiting(true);
    removeTimerRef.current = setTimeout(removeNow, EXIT_ANIMATION_MS);
  };

  if (isEditing) {
    return (
      <EditingShortcutSlot
        slotIndex={slotIndex}
        initialAgentId={agentId}
        availableAgents={availableAgents}
        isSaving={upsertMutation.isPending}
        onCancel={() => setIsEditing(false)}
        onConfirm={(nextAgentId) =>
          upsertMutation.mutate(
            { slotIndex, agentId: nextAgentId },
            {
              onSuccess: () => {
                setIsEditing(false);
                setJustSaved(true);
              },
            },
          )
        }
      />
    );
  }

  if (!agentId) {
    return (
      <EmptyShortcutSlot
        slotIndex={slotIndex}
        disabled={disabled}
        onAdd={() => setIsEditing(true)}
      />
    );
  }

  const filledAgent = availableAgents.find((a) => a.id === agentId) ?? null;

  const handleLaunch = () => {
    if (!filledAgent) return;
    const handler = launchHandlers[agentId];
    if (handler) {
      handler();
      return;
    }
    launchAgentWithWidget(navigate, agentId);
  };

  return (
    <FilledShortcutSlot
      slotIndex={slotIndex}
      agentId={agentId}
      agent={filledAgent}
      isRemoving={deleteMutation.isPending}
      onLaunch={handleLaunch}
      onEdit={() => setIsEditing(true)}
      onRemove={handleRemove}
      justSaved={justSaved}
      onEnterAnimationEnd={() => setJustSaved(false)}
      isExiting={isExiting}
    />
  );
}
